/**
 * preferencesStore — Shared UI preferences with localStorage persistence.
 * Follows the same pattern as themeStore.ts.
 */

import { createAppStore } from "./createAppStore";

type TableDensity = "compact" | "normal" | "comfortable";
type DateFormat = "relative" | "absolute";
type NotifEmailDigest = "off" | "daily" | "weekly";
/**
 * Floating widget choice — picks which (if any) widget renders in the
 * bottom-right corner of every dashboard. The slot is shared between the
 * Plerity Assistant chat (`assistant`) and the Tester Notes capture
 * (`tester-notes`). They never render simultaneously.
 *
 * Default: `assistant` for backward compatibility with apps already
 * shipping the chat widget. Apps that opt in to TesterNotes can advertise
 * its availability via `testerNotes.enabled` in their createShell config;
 * the user still has to flip this preference to actually swap.
 */
type BottomRightWidget = "assistant" | "tester-notes" | "none";

interface PreferencesState {
  sidebarCollapsed: boolean;
  tablePageSize: number;
  tableDensity: TableDensity;
  dateFormat: DateFormat;
  // Baseline notification preferences consumed by NotificationsSettings
  // widget and synced cross-device via useUserSettingsSync. Apps with
  // richer notification systems (e.g. ops) opt out via disableBaseTabs.
  notifEmailDigest: NotifEmailDigest;
  notifDesktopToasts: boolean;
  notifSoundEnabled: boolean;
  /** Bottom-right widget toggle. See type docs above. */
  bottomRightWidget: BottomRightWidget;
  // Capitan picker — shared between AI dashboard middle sidebar and
  // capitan-mobile (which already uses the same field names in
  // frontend/apps/capitan-mobile/src/stores/chatPrefsStore.ts). Backend
  // truth lives in user_capitan_preferences (migration 008).
  featuredAgentIds: string[];
  lastAgentId: string | null;
  lastModel: string | null;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  setTablePageSize: (size: number) => void;
  setTableDensity: (density: TableDensity) => void;
  setDateFormat: (format: DateFormat) => void;
  setNotifEmailDigest: (digest: NotifEmailDigest) => void;
  setNotifDesktopToasts: (enabled: boolean) => void;
  setNotifSoundEnabled: (enabled: boolean) => void;
  setBottomRightWidget: (value: BottomRightWidget) => void;
  setFeaturedAgentIds: (ids: string[]) => void;
  toggleFeaturedAgent: (id: string) => void;
  setLastAgentId: (id: string | null) => void;
  setLastModel: (model: string | null) => void;
}

const MAX_FEATURED_AGENTS = 20;

const STORAGE_KEY = "dashboard-preferences";

interface StoredPreferences {
  sidebarCollapsed?: boolean;
  tablePageSize?: number;
  tableDensity?: TableDensity;
  dateFormat?: DateFormat;
  notifEmailDigest?: NotifEmailDigest;
  notifDesktopToasts?: boolean;
  notifSoundEnabled?: boolean;
  bottomRightWidget?: BottomRightWidget;
  featuredAgentIds?: string[];
  lastAgentId?: string | null;
  lastModel?: string | null;
}

function migrateLegacyKeys(): Partial<StoredPreferences> {
  const migrated: Partial<StoredPreferences> = {};
  try {
    // Migrate StarChart's bare "sidebarCollapsed" key
    const legacy = localStorage.getItem("sidebarCollapsed");
    if (legacy !== null) {
      migrated.sidebarCollapsed = legacy === "true";
      localStorage.removeItem("sidebarCollapsed");
    }
  } catch {
    // ignore
  }
  return migrated;
}

function getInitialPreferences(): StoredPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as StoredPreferences;
    }
    // No stored preferences — check for legacy keys to migrate
    const migrated = migrateLegacyKeys();
    if (Object.keys(migrated).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      return migrated;
    }
  } catch {
    // localStorage unavailable or invalid JSON
  }
  return {};
}

function persistPreferences(partial: Partial<StoredPreferences>) {
  try {
    const current = getInitialPreferences();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...partial }));
  } catch {
    // ignore
  }
}

const initial = getInitialPreferences();

export const usePreferencesStore = createAppStore<PreferencesState>("preferences-store", (set) => ({
  sidebarCollapsed: initial.sidebarCollapsed ?? false,
  tablePageSize: initial.tablePageSize ?? 25,
  tableDensity: initial.tableDensity ?? "normal",
  dateFormat: initial.dateFormat ?? "relative",
  notifEmailDigest: initial.notifEmailDigest ?? "off",
  notifDesktopToasts: initial.notifDesktopToasts ?? false,
  notifSoundEnabled: initial.notifSoundEnabled ?? false,
  bottomRightWidget: initial.bottomRightWidget ?? "assistant",
  featuredAgentIds: initial.featuredAgentIds ?? [],
  lastAgentId: initial.lastAgentId ?? null,
  lastModel: initial.lastModel ?? null,

  setSidebarCollapsed: (collapsed) => {
    persistPreferences({ sidebarCollapsed: collapsed });
    set({ sidebarCollapsed: collapsed });
  },

  toggleSidebar: () => {
    set((state) => {
      const next = !state.sidebarCollapsed;
      persistPreferences({ sidebarCollapsed: next });
      return { sidebarCollapsed: next };
    });
  },

  setTablePageSize: (size) => {
    persistPreferences({ tablePageSize: size });
    set({ tablePageSize: size });
  },

  setTableDensity: (density) => {
    persistPreferences({ tableDensity: density });
    set({ tableDensity: density });
  },

  setDateFormat: (format) => {
    persistPreferences({ dateFormat: format });
    set({ dateFormat: format });
  },

  setNotifEmailDigest: (digest) => {
    persistPreferences({ notifEmailDigest: digest });
    set({ notifEmailDigest: digest });
  },

  setNotifDesktopToasts: (enabled) => {
    persistPreferences({ notifDesktopToasts: enabled });
    set({ notifDesktopToasts: enabled });
  },

  setNotifSoundEnabled: (enabled) => {
    persistPreferences({ notifSoundEnabled: enabled });
    set({ notifSoundEnabled: enabled });
  },

  setBottomRightWidget: (value) => {
    persistPreferences({ bottomRightWidget: value });
    set({ bottomRightWidget: value });
  },

  setFeaturedAgentIds: (ids) => {
    const capped = ids.slice(0, MAX_FEATURED_AGENTS);
    persistPreferences({ featuredAgentIds: capped });
    set({ featuredAgentIds: capped });
  },

  toggleFeaturedAgent: (id) => {
    set((state) => {
      const present = state.featuredAgentIds.includes(id);
      const next = present
        ? state.featuredAgentIds.filter((x) => x !== id)
        : [...state.featuredAgentIds, id].slice(0, MAX_FEATURED_AGENTS);
      persistPreferences({ featuredAgentIds: next });
      return { featuredAgentIds: next };
    });
  },

  setLastAgentId: (id) => {
    persistPreferences({ lastAgentId: id });
    set({ lastAgentId: id });
  },

  setLastModel: (model) => {
    persistPreferences({ lastModel: model });
    set({ lastModel: model });
  },
}));
